// src/lib/renderBody.tsx
// 実践レポート・発見ノートの本文を簡易マークダウンでレンダリングするユーティリティ。
// Server Component / Client Component どちらでも使用可能。
//
// 対応記法: **太字** / *斜体* / ### H3 / #### H4 /
//           番号付きリスト (1.) / 箇条書き (-) / 引用 (>) /
//           改行保持（\n → <br>） / 段落区切り（\n\n） / URL自動リンク化

import type { ReactNode } from "react";

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|\*(.+?)\*|(https?:\/\/[^\s<>"]+)/g;
  let last = 0;
  let matchIdx = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      parts.push(<strong key={`${keyPrefix}-b${matchIdx}`}>{m[1]}</strong>);
    } else if (m[2] !== undefined) {
      parts.push(<em key={`${keyPrefix}-i${matchIdx}`}>{m[2]}</em>);
    } else if (m[3] !== undefined) {
      const url = m[3];
      parts.push(
        <a
          key={`${keyPrefix}-a${matchIdx}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-violet-600 underline hover:text-violet-800 break-all"
        >
          {url}
        </a>
      );
    }
    last = m.index + m[0].length;
    matchIdx++;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export function renderBody(text: string): ReactNode {
  const blocks = text.split(/\n\n+/);
  return (
    <>
      {blocks.map((block, bi) => {
        const lines = block.split("\n");
        const first = lines[0];

        // H4（#### を先にチェック。### より長いため）
        if (first.startsWith("#### ")) {
          return (
            <h4 key={bi} className="text-sm font-semibold text-gray-800 mt-4 mb-1">
              {renderInline(first.slice(5), `${bi}`)}
            </h4>
          );
        }

        // H3
        if (first.startsWith("### ")) {
          return (
            <h3 key={bi} className="text-base font-bold text-gray-900 mt-5 mb-1">
              {renderInline(first.slice(4), `${bi}`)}
            </h3>
          );
        }

        // 番号付きリスト（1. から始まる行）
        if (/^\d+\. /.test(first)) {
          return (
            <ol key={bi} className="list-decimal list-outside ml-5 mb-3 space-y-1">
              {lines.map((line, li) => {
                const stripped = line.replace(/^\d+\. /, "");
                return (
                  <li key={li} className="text-sm leading-7 text-gray-800">
                    {renderInline(stripped, `${bi}-${li}`)}
                  </li>
                );
              })}
            </ol>
          );
        }

        // 箇条書き（- から始まる行）
        if (first.startsWith("- ")) {
          return (
            <ul key={bi} className="list-disc list-outside ml-5 mb-3 space-y-1">
              {lines.map((line, li) => {
                const stripped = line.startsWith("- ") ? line.slice(2) : line;
                return (
                  <li key={li} className="text-sm leading-7 text-gray-800">
                    {renderInline(stripped, `${bi}-${li}`)}
                  </li>
                );
              })}
            </ul>
          );
        }

        // 引用（> から始まる行）
        if (first.startsWith("> ")) {
          return (
            <blockquote
              key={bi}
              className="border-l-4 border-gray-300 pl-4 my-3 text-sm text-gray-600 italic"
            >
              {lines.reduce<ReactNode[]>((acc, line, li) => {
                const stripped = line.startsWith("> ") ? line.slice(2) : line;
                acc.push(...renderInline(stripped, `${bi}-${li}`));
                if (li < lines.length - 1) acc.push(<br key={`${bi}-br${li}`} />);
                return acc;
              }, [])}
            </blockquote>
          );
        }

        // デフォルト：段落（改行保持）
        const content = lines.reduce<ReactNode[]>((acc, line, li) => {
          acc.push(...renderInline(line, `${bi}-${li}`));
          if (li < lines.length - 1) acc.push(<br key={`${bi}-br${li}`} />);
          return acc;
        }, []);
        return (
          <p key={bi} className="mb-3 text-sm leading-7 text-gray-800 last:mb-0">
            {content}
          </p>
        );
      })}
    </>
  );
}
